import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
  Input,
  Textarea,
} from "@fluentui/react-components";
import { CheckmarkRegular, DismissRegular } from "@fluentui/react-icons";
import { DialogFooterAction } from "components/dialogFooterAction/DialogFooterAction";
import { useEffect, useState } from "react";
import {
  useSharedDialogContentStyles,
  useSharedDialogFooterStyles,
} from "theme/sharedStyles";
import { useWorkspaceClientInvitationsStyles } from "./WorkspaceClientInvitations.styles";
import { IInviteClientDialogProps } from "./WorkspaceClientInvitations.types";

const EMAIL_PATTERN = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
// Azure AD truncates anything longer than this in the invitation email body.
const MESSAGE_MAX_LENGTH = 256;
const MESSAGE_ROWS = 3;

export const InviteClientDialog = ({
  isOpen,
  isInviting,
  errorMessage,
  onDismiss,
  onConfirm,
}: IInviteClientDialogProps): JSX.Element => {
  const contentStyles = useSharedDialogContentStyles();
  const footerStyles = useSharedDialogFooterStyles();
  const styles = useWorkspaceClientInvitationsStyles();
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [message, setMessage] = useState("");
  const [emailError, setEmailError] = useState("");

  useEffect(() => {
    if (isOpen) {
      setEmail("");
      setDisplayName("");
      setMessage("");
      setEmailError("");
    }
  }, [isOpen]);

  const handleConfirm = () => {
    const trimmedEmail = email.trim();
    if (!EMAIL_PATTERN.test(trimmedEmail)) {
      setEmailError("Enter a valid email address");
      return;
    }
    onConfirm(
      trimmedEmail,
      displayName.trim() || undefined,
      message.trim() || undefined,
    );
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(_event, data) => {
        if (!data.open) onDismiss();
      }}
    >
      <DialogSurface className={contentStyles.dialogSurface}>
        <DialogBody>
          <DialogTitle
            action={
              <Button
                appearance="subtle"
                icon={<DismissRegular aria-hidden="true" />}
                aria-label="Close dialog"
                onClick={onDismiss}
              />
            }
          >
            Invite client
          </DialogTitle>
          <DialogContent>
            <div className={contentStyles.formContainer}>
              <Field
                label="Email"
                required
                validationState={emailError ? "error" : "none"}
                validationMessage={emailError || undefined}
              >
                <Input
                  type="email"
                  value={email}
                  onChange={(_event, data) => {
                    setEmail(data.value);
                    if (emailError) setEmailError("");
                  }}
                  placeholder="e.g. jane.smith@client.com"
                  autoFocus
                  aria-label="Client email address"
                />
              </Field>
              <Field label="Name (optional)">
                <Input
                  value={displayName}
                  onChange={(_event, data) => setDisplayName(data.value)}
                  placeholder="e.g. Jane Smith"
                  aria-label="Client name"
                />
              </Field>
              <Field
                label="Message (optional)"
                hint={`Included in the invitation email. ${message.length}/${MESSAGE_MAX_LENGTH} characters.`}
              >
                <Textarea
                  value={message}
                  onChange={(_event, data) => setMessage(data.value)}
                  maxLength={MESSAGE_MAX_LENGTH}
                  rows={MESSAGE_ROWS}
                  placeholder="e.g. Here is the workspace for our discovery phase."
                  aria-label="Invitation message"
                />
              </Field>
              {errorMessage && (
                <span role="alert" className={styles.errorMessage}>
                  {errorMessage}
                </span>
              )}
            </div>
          </DialogContent>
        </DialogBody>
        <div className={footerStyles.dialogFooter}>
          <DialogFooterAction
            icon={<CheckmarkRegular />}
            tooltip="Send invitation"
            dimmed={!email.trim() || isInviting}
            disabled={isInviting}
            onClick={handleConfirm}
          />
        </div>
      </DialogSurface>
    </Dialog>
  );
};
